import { CoreFetch } from '../../api/index';
import { ReportApi } from '../../api/server/apis/ReportApi';
import { ProviderClaimResponsibilityReportItem } from '../../api/server/models/ProviderClaimResponsibilityReportItem';

/**
 * Everything the "Active Claims By Responsibility" widget reads, behind one interface: the provider Claims page
 * and the Support claims list each wire their own, and the widget does not know which (the `IDenialsApi` shape,
 * FE-155).
 *
 * ## The endpoint
 *
 * `GET /provider/report/responsibility?start&end` — one row per (responsibility type, category) with a
 * distinct-claim count and summed charges, over ACTIVE claims of every claim type. `start` is inclusive and `end`
 * EXCLUSIVE; it answers 400 when `end <= start` or the range is over one calendar year (sandbox, 2026-10-06).
 * Responsibility is each claim's current state, not its state on the create date.
 *
 * ## Support
 *
 * There is no Support version: `/support/report/responsibility` answers 404 (sandbox, 2026-10-07), as every
 * `/support/report/*` does except QuickSight. Support therefore reads the provider report for the account it has
 * switched to — the route Denials already takes for its reports (`denials-api.ts`: `/provider/report/*` answers
 * 200 to a Support token, with that account's rows; production, 2026-10-06). The account is carried by the token,
 * not by a parameter. Not yet measured for THIS report with a Support token: the sandbox Support session had
 * expired when this was written (2026-10-09).
 *
 * Why not code against an assumed `/support/report/responsibility`: until it ships, every request would answer
 * 404, and core's auth middleware opens the blocking "Error encountered" dialog on any failed response — on every
 * load of the Support claims list. When a Support endpoint ships, point `supportClaimResponsibilityApi` at it;
 * nothing else changes.
 */
export interface IClaimResponsibilityApi {
    fetchReport(params: { start: string; end: string }): Promise<ProviderClaimResponsibilityReportItem[] | undefined>;
}

const providerReport: IClaimResponsibilityApi = {
    fetchReport: params => CoreFetch<ReportApi>(ReportApi).getClaimResponsibilityReport(params),
};

export const providerClaimResponsibilityApi: IClaimResponsibilityApi = providerReport;

/** The provider report, read with the Support token for the account Support has switched to — see above. */
export const supportClaimResponsibilityApi: IClaimResponsibilityApi = providerReport;
